import { Popover } from '@base-ui/react/popover';
import { Compass } from 'lucide-react';
import { useCallback, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@/shared/ui/button';

import { FIELD_TOUR_CHAPTERS, FIELD_TOUR_STEPS } from '../field-tour-steps';

import {
  findTourTarget,
  sameTourContext,
  scrollTourTarget,
  tourMarkerPosition,
  visibleTourRect,
} from './field-tour-position';

import type { TourRect } from './field-tour-position';
import type { KeyboardEvent, RefObject } from 'react';

import './field-tour.css';

export { FIELD_TOUR_STEPS } from '../field-tour-steps';

function tourStep(index: number) {
  const item = FIELD_TOUR_STEPS[index] ?? FIELD_TOUR_STEPS[0];
  if (!item) throw new Error('The Workbench guide needs at least one step.');
  return item;
}

interface TourLayout {
  ready: boolean;
  docked: boolean;
  target: HTMLElement | null;
  rect: TourRect | null;
  markers: { index: number; left: number; top: number }[];
}

function useTourLayout(rail: RefObject<HTMLDivElement | null>, index: number) {
  const [layout, setLayout] = useState<TourLayout>({
    ready: false,
    docked: true,
    target: null,
    rect: null,
    markers: [],
  });
  useLayoutEffect(() => {
    const scope = rail.current?.closest<HTMLElement>('.field-page');
    const current = tourStep(index);
    if (!scope) {
      setLayout({ ready: true, docked: true, target: null, rect: null, markers: [] });
      return;
    }
    const page = scope;
    let frame = 0;
    let scrolled: HTMLElement | null = null;
    let settlingFrames = 0;
    let previousViewport = '';
    let observedTarget: HTMLElement | null = null;
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    function update() {
      frame = 0;
      const viewport = {
        width: window.visualViewport?.width ?? window.innerWidth,
        height: window.visualViewport?.height ?? window.innerHeight,
      };
      const viewportKey = `${viewport.width}:${viewport.height}`;
      if (viewportKey !== previousViewport) settlingFrames = 2;
      previousViewport = viewportKey;
      const target = findTourTarget(page, current.target, current.fallbackTarget);
      if (target && target !== scrolled) settlingFrames = 2;
      if (target && (target !== scrolled || settlingFrames > 0)) {
        scrollTourTarget(target, page);
        scrolled = target;
        settlingFrames -= 1;
      }
      if (target !== observedTarget) {
        resize?.disconnect();
        resize?.observe(page);
        if (rail.current?.parentElement) resize?.observe(rail.current.parentElement);
        for (let parent = target; parent && parent !== page; parent = parent.parentElement)
          resize?.observe(parent);
        observedTarget = target;
      }
      const rect = target ? visibleTourRect(target, page) : null;
      const docked = viewport.width <= 740 || viewport.height <= 600 || !rect;
      const seen = new Set<HTMLElement>();
      if (target) seen.add(target);
      const markers: TourLayout['markers'] = [];
      FIELD_TOUR_STEPS.forEach((item, itemIndex) => {
        if (itemIndex === index || !sameTourContext(current.destination, item.destination)) return;
        // Optional targets use the bubble's contextual fallback, not duplicate cloud markers.
        const element = findTourTarget(page, item.target);
        if (!element || seen.has(element)) return;
        seen.add(element);
        const visible = visibleTourRect(element, page);
        if (visible) markers.push({ index: itemIndex, ...tourMarkerPosition(visible, viewport) });
      });
      setLayout((old) => {
        if (
          old.ready &&
          old.docked === docked &&
          old.target === target &&
          JSON.stringify(old.rect) === JSON.stringify(rect) &&
          JSON.stringify(old.markers) === JSON.stringify(markers)
        )
          return old;
        return { ready: true, docked, target, rect, markers };
      });
      // A dock reserves space on the first render. Align again once that layout is committed.
      if (settlingFrames > 0) schedule();
    }
    function schedule() {
      if (!frame) frame = requestAnimationFrame(update);
    }
    update();
    resize?.observe(page);
    if (rail.current?.parentElement) resize?.observe(rail.current.parentElement);
    const mutations = new MutationObserver(schedule);
    mutations.observe(page, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['hidden', 'inert', 'aria-hidden', 'class', 'style', 'data-field-tour'],
    });
    page.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('scroll', schedule);
    return () => {
      cancelAnimationFrame(frame);
      resize?.disconnect();
      mutations.disconnect();
      page.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('scroll', schedule);
    };
  }, [index, rail]);
  return layout;
}

export function FieldTourInvitation({
  onStart,
  onDismiss,
}: {
  onStart: () => void;
  onDismiss: () => void;
}) {
  const titleId = useId();
  return (
    <section className="field-tour-invitation" aria-labelledby={titleId}>
      <Compass size={20} aria-hidden="true" />
      <div className="field-tour-invitation-copy">
        <h2 id={titleId}>New to the Workbench?</h2>
        <p>
          Click the numbered callouts to explore, or follow Next through the brief, colleagues,
          building and reviews.
        </p>
      </div>
      <div className="field-tour-invitation-actions">
        <Button type="button" size="sm" onClick={onStart}>
          Take a tour
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDismiss}>
          Not now
        </Button>
      </div>
    </section>
  );
}

export function FieldTour({
  step,
  onStep,
  onClose,
  onFinish,
}: {
  step: number;
  onStep: (index: number) => void;
  onClose: () => void;
  onFinish: () => void;
}) {
  const currentIndex = Number.isFinite(step)
    ? Math.min(Math.max(Math.trunc(step), 0), FIELD_TOUR_STEPS.length - 1)
    : 0;
  const current = tourStep(currentIndex);
  const last = currentIndex === FIELD_TOUR_STEPS.length - 1;
  const rail = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const focusedStep = useRef<number | null>(null);
  const previousHeading = useRef<HTMLHeadingElement | null>(null);
  const restoreBubbleFocus = useRef(false);
  const titleId = useId();
  const descriptionId = useId();
  const layout = useTourLayout(rail, currentIndex);
  useLayoutEffect(() => {
    let frame = requestAnimationFrame(() => {
      // A destination can mount after the step and replace its provisional dock with a portal.
      // Finish that one user-requested focus move after layout; never take focus from a form.
      frame = requestAnimationFrame(() => {
        const active = document.activeElement;
        if (
          active === document.body ||
          rail.current?.contains(active) ||
          active?.closest('.field-tour-bubble')
        )
          previousHeading.current?.focus({ preventScroll: true });
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [currentIndex]);
  const heading = useCallback(
    (node: HTMLHeadingElement | null) => {
      if (!node) {
        restoreBubbleFocus.current = !!previousHeading.current
          ?.closest('.field-tour-bubble')
          ?.contains(document.activeElement);
        previousHeading.current = null;
        return;
      }
      previousHeading.current = node;
      if (!layout.ready || (focusedStep.current === currentIndex && !restoreBubbleFocus.current))
        return;
      restoreBubbleFocus.current = false;
      focusedStep.current = currentIndex;
      if (content.current) content.current.scrollTop = 0;
      node.focus({ preventScroll: true });
    },
    [currentIndex, layout.ready],
  );
  const anchorRect = layout.rect;
  const anchor = useMemo(
    () =>
      anchorRect
        ? {
            getBoundingClientRect: () =>
              DOMRect.fromRect({
                x: anchorRect.left,
                y: anchorRect.top,
                width: anchorRect.width,
                height: anchorRect.height,
              }),
            ...(layout.target ? { contextElement: layout.target } : {}),
          }
        : null,
    [anchorRect, layout.target],
  );
  function escape(event: KeyboardEvent) {
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    event.preventDefault();
    event.stopPropagation();
    onClose();
  }
  const bubble = (
    <section className="field-tour-bubble" aria-label="Workbench tour" onKeyDown={escape}>
      <div ref={content} className="field-tour-content">
        <p className="field-tour-progress" role="status" aria-atomic="true">
          <span className="field-tour-badge" aria-hidden="true">
            {currentIndex + 1}
          </span>
          Step {currentIndex + 1} of {FIELD_TOUR_STEPS.length} ·{' '}
          {FIELD_TOUR_CHAPTERS.find((item) => item.id === current.chapter)?.label}
        </p>
        <h2 ref={heading} id={titleId} tabIndex={-1} aria-describedby={descriptionId}>
          {current.title}
        </h2>
        <p id={descriptionId} className="field-tour-description">
          {current.description}
        </p>
        <ul className="field-tour-details">
          {current.details.map((detail) => (
            <li key={detail}>{detail}</li>
          ))}
        </ul>
        <p className="field-tour-tip">{current.tip}</p>
        {layout.ready && !layout.target && (
          <p className="field-tour-context">
            This control is not available in the current view. You can continue the tour.
          </p>
        )}
        {layout.target && !layout.rect && (
          <button
            className="field-tour-show-target"
            type="button"
            onClick={() => {
              const page = rail.current?.closest<HTMLElement>('.field-page');
              if (page && layout.target) scrollTourTarget(layout.target, page);
            }}
          >
            Show this control
          </button>
        )}
      </div>
      <div className="field-tour-actions">
        <Button type="button" size="sm" variant="ghost" onClick={onClose}>
          {last ? 'Close tour' : 'Skip tour'}
        </Button>
        <div>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={currentIndex === 0}
            onClick={() => onStep(currentIndex - 1)}
          >
            Back
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => (last ? onFinish() : onStep(currentIndex + 1))}
          >
            {last ? 'Return to brief' : 'Next'}
          </Button>
        </div>
      </div>
    </section>
  );

  return (
    <div
      className={layout.docked ? 'field-tour-guide field-tour-guide-docked' : 'field-tour-guide'}
    >
      <div
        ref={rail}
        className="field-tour-rail"
        role="region"
        aria-label="Tour navigation"
        onKeyDown={escape}
      >
        <span className="field-tour-label">
          <Compass size={16} aria-hidden="true" /> Workbench guide
        </span>
        <nav className="field-tour-chapters" aria-label="Tour chapters">
          {FIELD_TOUR_CHAPTERS.map((chapter, index) => (
            <button
              key={chapter.id}
              type="button"
              aria-current={chapter.id === current.chapter ? 'step' : undefined}
              aria-label={`Chapter ${index + 1}: ${chapter.label}`}
              onClick={() =>
                onStep(FIELD_TOUR_STEPS.findIndex((item) => item.chapter === chapter.id))
              }
            >
              <span aria-hidden="true">{index + 1}</span>
              <span className="field-tour-chapter-name">{chapter.label}</span>
            </button>
          ))}
        </nav>
        <select
          className="field-tour-select"
          aria-label="Tour step"
          value={currentIndex}
          onChange={(event) => onStep(Number(event.target.value))}
        >
          {FIELD_TOUR_CHAPTERS.map((chapter) => (
            <optgroup key={chapter.id} label={chapter.label}>
              {FIELD_TOUR_STEPS.map((item, index) =>
                item.chapter === chapter.id ? (
                  <option key={item.id} value={index}>
                    {index + 1}. {item.shortLabel}
                  </option>
                ) : null,
              )}
            </optgroup>
          ))}
        </select>
      </div>
      {layout.docked ? (
        bubble
      ) : (
        <Popover.Root
          open
          modal={false}
          onOpenChange={(_open, details) => {
            // Navigation elsewhere remains usable. Only the scoped Escape or explicit buttons close.
            details.cancel();
            details.allowPropagation();
          }}
        >
          <Popover.Portal>
            <Popover.Positioner
              className="field-tour-positioner"
              anchor={anchor}
              positionMethod="fixed"
              side="bottom"
              align="start"
              sideOffset={12}
              collisionPadding={12}
              collisionAvoidance={{ side: 'flip', align: 'shift', fallbackAxisSide: 'end' }}
            >
              <Popover.Popup
                className="field-tour-popup"
                role="presentation"
                initialFocus={false}
                finalFocus={false}
              >
                {bubble}
                <Popover.Arrow className="field-tour-arrow">
                  <svg width="18" height="10" viewBox="0 0 18 10" aria-hidden="true">
                    <path d="M0 10 9 1 18 10" />
                  </svg>
                </Popover.Arrow>
              </Popover.Popup>
            </Popover.Positioner>
          </Popover.Portal>
        </Popover.Root>
      )}
      {layout.ready &&
        createPortal(
          <div className="field-tour-overlays">
            {layout.rect && (
              <div
                className="field-tour-highlight"
                aria-hidden="true"
                style={{
                  left: layout.rect.left - 3,
                  top: layout.rect.top - 3,
                  width: layout.rect.width + 6,
                  height: layout.rect.height + 6,
                }}
              />
            )}
            {layout.markers.map((marker) => (
              <button
                key={marker.index}
                className="field-tour-marker"
                type="button"
                aria-label={`Show step ${marker.index + 1}: ${tourStep(marker.index).shortLabel}`}
                style={{ left: marker.left, top: marker.top }}
                onClick={() => onStep(marker.index)}
                onKeyDown={escape}
              >
                {marker.index + 1}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
